import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  TextTeaserHeadlineSize,
  TextTeaserSchemeDefinition,
  TextTeaserSchemeName
} from "./constants/text-teaser-types";
import { TEXT_TEASER_SCHEME_CONFIG, TEXT_TEASER_SCHEME_MAP } from "./constants/text-teaser-constants";
import { HeadlineTag } from "../headline/constants/headline-types";
import { clampHeadlineSizeTo, useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { getNestedChildrenOf, getFirstNestedChild, getNestedChildren } from "../../utils/dom-helper";
import { createButtonMapDefinition } from "../../utils/scheme-helper";
import { BackgroundColor } from "../background/constants/background-types";

import {
  HeadlineAlignment,
  TEXT_TEASER_CONTENT_ALIGNMENT,
  TEXT_TEASER_CONTENT_WITH_NUMBER_ALIGNMENT,
  TEXT_TEASER_DEFAULT_HEADLINE_ALIGNMENT,
  TEXT_TEASER_DEFAULT_HEADLINE_WITH_NUMBER_ALIGNMENT,
  TEXT_TEASER_DEFAULT_PROMO_FLAG_ALIGNMENT,
  TEXT_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  TEXT_TEASER_HEADLINE_SIZE,
  TEXT_TEASER_HEADLINE_SIZES,
  TEXT_TEASER_NUMBER_ALIGNMENT,
  TEXT_TEASER_PICTOGRAM_ALIGNMENT,
  TEXT_TEASER_PICTOGRAM_WITH_NUMBER_ALIGNMENT,
  TextTeaserPromoFlagAlignment,
  TextTeaserViolatorHorizontalPosition,
  HEADLINE_ALIGNMENT_CENTER,
  HEADLINE_ALIGNMENT_LEFT
} from "../../constants/global-constants";
import { TextTeaserSchemeConfiguration } from "../../constants/colors";

/**
 * @slot [number] - Holds <eon-ui-number>.
 * @slot [button] - Holds <eon-ui-button>.
 * @slot [imageTeaser] - Holds <eon-ui-image>.
 * @slot [unnamed] - Holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-text-teaser",
  styleUrl: "text-teaser.scss",
  shadow: true
})
export class TextTeaser implements ColorableComponent<TextTeaserSchemeConfiguration<TextTeaserSchemeDefinition>> {
  schemeConfig: TextTeaserSchemeConfiguration<TextTeaserSchemeDefinition> = TEXT_TEASER_SCHEME_CONFIG;
  schemeMap = TEXT_TEASER_SCHEME_MAP;

  private hasButton: boolean;
  private hasViolator: boolean;

  @State() activeScheme: TextTeaserSchemeDefinition;
  @State() seoHeadlineTag: string;
  @State() pictogramAlignment: string;
  @State() contentAlignment: string;
  @State() numberAlignment: string;
  @State() isImageAvailable: boolean;
  @State() isContentAvailable: boolean;

  @Element() hostElement: HTMLEonUiTextTeaserElement;

  /**
   * Raised when the CTA in Text Teaser has been clicked.
   */
  @Event() textTeaserClick: EventEmitter;

  /**
   * Raised when the component has been loaded.
   */
  @Event() textTeaserLoad: EventEmitter;

  /**
   * The color scheme of the Teaser
   */
  @Prop({ reflect: true }) scheme: TextTeaserSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The size of the headline inside the teaser
   */
  @Prop() align: HeadlineAlignment = TEXT_TEASER_DEFAULT_HEADLINE_ALIGNMENT;

  /**
   * The size of the headline inside the teaser
   */
  @Prop({ mutable: true }) size: TextTeaserHeadlineSize = TEXT_TEASER_HEADLINE_SIZE;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * name of the eon-ui-pictogram inside the teaser (see eon-ui-pictogram)
   */
  @Prop() pictogram: string;

  /**
   * To hide and show slotted button component
   */
  @Prop({ reflect: true }) showButton: boolean = true;

  /**
   * Defines the position of violator on X
   */
  // prettier-ignore
  @Prop() violatorHorizontalPosition: TextTeaserViolatorHorizontalPosition = TEXT_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Is promo-flag available or not
   */
  @Prop({ reflect: true, mutable: true }) hasPromoFlag: boolean;

  /**
   * Alignment of promoflag if exists
   */
  @State() promoFlagAlignment: TextTeaserPromoFlagAlignment = TEXT_TEASER_DEFAULT_PROMO_FLAG_ALIGNMENT;

  /**
   * PromoFlag small or normal if exists
   */
  @Prop({ reflect: true, mutable: true }) promoFlagSmall: boolean;

  /**
   * Is a teaser-number variant
   */
  @State() isTeaserNumber: boolean;

  componentWillLoad() {
    this.applyScheme();
    this.passSchemeToChildren();
    this.handleHeadlineTag();
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");
    this.isTeaserNumber = !!this.hostElement.querySelector('[slot="number"]');
    this.hasButton = !!this.hostElement.querySelector('[slot="button"]');
    this.isContentAvailable = !!this.hostElement.querySelector(':not([slot="button"])');
    this.handleNumberTeaserAlignment();
    this.handleSlottedImage();
    const promoFlag = getFirstNestedChild(this.hostElement, "eon-ui-promo-flag") as HTMLEonUiPromoFlagElement;
    this.hasPromoFlag = !!promoFlag;
    this.promoFlagAlignment = promoFlag?.alignment;
    this.promoFlagSmall = promoFlag?.small;
  }

  componentDidLoad() {
    this.setCssVariables();
    if (this.hasPromoFlag) {
      this.initPromoFlag();
    }
    this.textTeaserLoad.emit();
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.textTeaserClick.emit(event);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("scheme")
  // This method is responsible to keep slotted components in sync with page-header scheme
  // when it changes during the life of the component. This mostly applies to storybook controls in
  // canvas tab where changes to props don't trigger component reloading (In docs tab it does).
  passSchemeToChildren() {
    if (!!this.scheme) {
      const backgroundColor = this.innerBackground;
      const buttonSchemeMapDefinition = createButtonMapDefinition();
      let buttons = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));
      buttons.forEach((slotButton) =>
        slotButton.setAttribute("scheme", buttonSchemeMapDefinition[backgroundColor as BackgroundColor] ?? "")
      );
    }
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.contentBackgroundColor;
  }

  @Watch("size")
  clampSize() {
    this.size = clampHeadlineSizeTo(TEXT_TEASER_HEADLINE_SIZES, this.size) as TextTeaserHeadlineSize;
  }

  @Watch("headlineHtmlTag")
  @Watch("size")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, this.size);
  }

  @Watch("align")
  handleNumberTeaserAlignment() {
    /**
     * When the TextTeaser alignment change to right align,
     * Pictogram + Headline + Number change to right alignment,
     * the content below remains left aligned
     */
    if (this.align) {
      this.pictogramAlignment = this.align;
      this.contentAlignment =
        this.align == (HEADLINE_ALIGNMENT_CENTER || HEADLINE_ALIGNMENT_LEFT) ? this.align : "left";
      this.numberAlignment = this.align;
    } else if (!!this.isTeaserNumber) {
      this.pictogramAlignment = TEXT_TEASER_PICTOGRAM_WITH_NUMBER_ALIGNMENT;
      this.contentAlignment = TEXT_TEASER_CONTENT_WITH_NUMBER_ALIGNMENT;
      this.numberAlignment = TEXT_TEASER_NUMBER_ALIGNMENT;
      this.align = TEXT_TEASER_DEFAULT_HEADLINE_WITH_NUMBER_ALIGNMENT;
    } else {
      this.pictogramAlignment = TEXT_TEASER_PICTOGRAM_ALIGNMENT;
      this.contentAlignment = TEXT_TEASER_CONTENT_ALIGNMENT;
      this.numberAlignment = TEXT_TEASER_NUMBER_ALIGNMENT;
      this.align = TEXT_TEASER_DEFAULT_HEADLINE_ALIGNMENT;
    }
  }

  handleSlottedImage() {
    let slottedImage = getFirstNestedChild(this.hostElement, "eon-ui-image") as HTMLEonUiImageElement;
    this.isImageAvailable = !!slottedImage;
  }

  initPromoFlag() {
    const isInitialized =
      this.hostElement.hasAttribute("promo-flag-lg") || this.hostElement.hasAttribute("promo-flag-sm");

    if (isInitialized) {
      return;
    }

    const siblingTeasers = Array.from(
      getNestedChildren(this.hostElement.closest("eon-ui-grid-control"), "eon-ui-text-teaser")
    );
    let promoFlagLarge: boolean = false;

    siblingTeasers.forEach((elm) => {
      if (promoFlagLarge) {
        return;
      }

      if (elm.hasAttribute("has-promo-flag")) {
        promoFlagLarge = elm.hasAttribute("promo-flag-small") ? false : true;
      }
    });

    siblingTeasers.forEach((elm) => {
      const attribute = promoFlagLarge ? "promo-flag-lg" : "promo-flag-sm";
      elm.setAttribute(attribute, "true");
    });
  }

  render() {
    return (
      <Host>
        <eon-ui-background color={this.activeScheme.contentBackgroundColor}>
          {this.hasPromoFlag && (
            <div
              class={pfx({
                "promo-flag": true,
                [this.promoFlagAlignment]: true
              })}
            >
              <slot name="promo-flag" />
            </div>
          )}
          <div
            class={pfx({
              "text-teaser-wrapper": true,
              [`promo-${this.promoFlagAlignment}`]: !!this.hasPromoFlag,
              [`promo-with-no-image`]: !this.isImageAvailable
            })}
          >
            {this.isImageAvailable && (
              <div class={pfx("text-teaser-image")}>
                {this.hasViolator && (
                  <div
                    class={pfx({
                      "text-teaser-violator": true,
                      [this.violatorHorizontalPosition]: true
                    })}
                  >
                    <slot name="violator"></slot>
                  </div>
                )}
                <slot name="imageTeaser" />
              </div>
            )}

            <div
              class={pfx({
                ["text-teaser-container"]: true,
                "text-teaser-content-copy-only": !this.pictogram && !this.headlineText
              })}
            >
              <div
                class={pfx({
                  "text-teaser-content": true,
                  "text-teaser-number-content": this.isTeaserNumber,
                  [`alignment-${this.align}`]: !!this.align
                })}
              >
                {this.hasViolator && !this.isImageAvailable && (
                  <div
                    class={pfx({
                      "text-teaser-violator": true,
                      [this.violatorHorizontalPosition]: true
                    })}
                  >
                    <slot name="violator"></slot>
                  </div>
                )}
                {this.pictogram && (
                  <div
                    class={pfx({
                      "pictogram-wrapper": true,
                      [`alignment-${this.pictogramAlignment}`]: true
                    })}
                  >
                    <eon-ui-pictogram name={this.pictogram}></eon-ui-pictogram>
                  </div>
                )}
                {this.headlineText && (
                  <eon-ui-headline
                    class={pfx({
                      ["no-pictogram"]: this.hasViolator ? !this.pictogram && this.isImageAvailable : !this.pictogram,
                      "no-content": !this.isContentAvailable
                    })}
                    margin={false}
                    alignment={this.align}
                    size={this.size}
                    headline-html-tag={this.seoHeadlineTag}
                    text={this.headlineText}
                  ></eon-ui-headline>
                )}
                {this.isTeaserNumber && (
                  <div
                    class={pfx({
                      ["number"]: true,
                      [`alignment-${this.numberAlignment}`]: true
                    })}
                  >
                    <slot name="number" />
                  </div>
                )}
                <div
                  class={pfx({
                    ["content"]: true,
                    [`alignment-${this.contentAlignment}`]: true
                  })}
                >
                  <slot />
                </div>
                {this.hasButton && (
                  <div
                    class={pfx({
                      ["button-wrapper"]: true,
                      [`alignment-${this.contentAlignment}`]: true
                    })}
                  >
                    <slot name="button" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </eon-ui-background>
      </Host>
    );
  }
}
